"use client";

import type { PostHog } from "posthog-js";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { registerPostHogBaseline } from "./baseline";
import { getPostHogEnv, isPostHogEnabled } from "./env";

const PostHogClientContext = createContext<PostHog | null>(null);

export function usePostHogClient(): PostHog | null {
  return useContext(PostHogClientContext);
}

/**
 * Initialises PostHog and wraps the component tree with the PostHog React
 * context so downstream components can use `usePostHog()` and friends.
 *
 * Enabled in configured Vercel production and preview environments — see
 * `isPostHogEnabled()` in `./env.ts`. When disabled the provider still
 * renders its children (no-op wrapper) so the rest of the app is unaffected.
 *
 * The PostHog instance is scoped to the component lifecycle via a ref. On
 * unmount (e.g. HMR, tests) `posthog.reset()` is called to clean up
 * autocapture, session recording, and internal state.
 */
export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const [enabled] = useState(() => isPostHogEnabled());
  const [PHProvider, setPHProvider] = useState<React.ComponentType<{ client: PostHog; children: React.ReactNode }> | null>(null);
  const [client, setClient] = useState<PostHog | null>(null);
  const instanceRef = useRef<PostHog | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const env = getPostHogEnv();
    if (!env) return;

    let cancelled = false;
    (async () => {
      const { default: posthog } = await import("posthog-js");
      if (cancelled) return;
      posthog.init(env.key, {
        api_host: env.host,
        capture_pageview: "history_change",
        capture_pageleave: "if_capture_pageview",
        autocapture: {
          css_selector_allowlist: ["[ph-autocapture]"],
        },
        // WIT-1751 d8: frustration signals. Rage clicks ($rageclick) and dead
        // clicks ($dead_click) surface the interactions where users hammer a
        // frozen surface — the qualitative twin of the latency dashboards.
        rageclick: true,
        capture_dead_clicks: true,
        capture_exceptions: {
          capture_unhandled_errors: true,
          capture_unhandled_rejections: true,
          capture_console_errors: false,
        },
        disable_session_recording: false,
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: "*",
        },
        capture_performance: {
          web_vitals: true,
          network_timing: true,
        },
        persistence: "localStorage+cookie",
        person_profiles: "identified_only",
        loaded: (ph) => {
          if (cancelled) return;
          if (process.env.NODE_ENV === "development") ph.debug();
          setClient(posthog);
        },
      });
      // WIT-1237: stamp EVERY event (web vitals, pageviews, autocapture —
      // not just interaction_timing) with the deploy SHA as a super property
      // so any PostHog insight can be release-annotated and a regression can
      // be attributed to a deploy. Matches the per-event `release` property
      // use-interaction-timing.ts already sends. Web-vitals capture itself is
      // already enabled above via `capture_performance.web_vitals`.
      registerPostHogBaseline(posthog);
      instanceRef.current = posthog;

      const { PostHogProvider: Provider } = await import("posthog-js/react");
      if (cancelled) return;
      setPHProvider(() => Provider);
    })();

    return () => {
      cancelled = true;
      if (instanceRef.current) {
        // Clean up PostHog on unmount (HMR, tests) to stop autocapture,
        // session recording, and prevent stale instances on remount.
        instanceRef.current.reset();
        instanceRef.current = null;
      }
    };
  }, [enabled]);

  if (!enabled || !PHProvider || !client) {
    return (
      <PostHogClientContext.Provider value={client}>
        {children}
      </PostHogClientContext.Provider>
    );
  }
  return (
    <PostHogClientContext.Provider value={client}>
      <PHProvider client={client}>{children}</PHProvider>
    </PostHogClientContext.Provider>
  );
}
